import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { groupDeliveriesByStatus, tonnesOf } from "../deliveriesBoard";
import type { Delivery } from "../../api/deliveries";
import { DeliveryStatusBadge } from "../../components/DeliveryStatusBadge";
import { DeliveryNoContractTag } from "../../components/DeliveryNoContractTag";
import type { CSSProperties } from "react";

/**
 * The deliveries board.
 *
 * **No drag and drop, on purpose.** Every transition here carries data — the
 * weighbridge figures, the analysis, the settled amount — so a card cannot move
 * to the next column without somebody typing something first. Dropping a card
 * would have to pop a form, which is the detail page. So the board shows where
 * things stand, and the card links to where the work happens.
 */
export function DeliveriesBoard({ deliveries }: { deliveries: Delivery[] }) {
  const { t, i18n } = useTranslation();
  const columns = groupDeliveriesByStatus(deliveries);

  return (
    <div style={boardStyle}>
      {columns.map((col) => (
        <section key={col.status} style={columnStyle}>
          <header style={columnHead}>
            <DeliveryStatusBadge status={col.status} />
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {col.count} · {col.sumTonnes.toFixed(2)} t
            </span>
          </header>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {col.deliveries.map((d) => (
              <Link key={d.id} to={`/procurement/deliveries/${d.id}`} style={cardStyle}>
                <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>{d.companyName}</span>
                <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {/* Uvezen prijem nosi dan prijema a ne najavu; prazan datum bi inače bio „Invalid Date". */}
                  {(d.receivedOn ?? d.announcedDate)
                    ? new Date((d.receivedOn ?? d.announcedDate) as string).toLocaleDateString(i18n.resolvedLanguage)
                    : "—"}{" "}· {tonnesOf(d).toFixed(2)} t
                </span>
                {d.vehiclePlate && (
                  <span style={{ fontSize: 11, color: "var(--ink-3)", fontFamily: "var(--mono)" }}>
                    {d.vehiclePlate}
                  </span>
                )}
                {d.weighbridgeTicketNo && (
                  <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{d.weighbridgeTicketNo}</span>
                )}
                <DeliveryNoContractTag delivery={d} />
              </Link>
            ))}
            {col.count === 0 && (
              <span style={{ fontSize: 12, color: "var(--ink-4)", padding: "8px 4px" }}>
                {t("deliveries.board.emptyColumn", "Nothing here")}
              </span>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

const boardStyle: CSSProperties = {
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(220px, 1fr)",
  gap: 12,
  overflowX: "auto",
  paddingBottom: 8,
};
const columnStyle: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 10,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minWidth: 220,
};
const columnHead: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};
const cardStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "10px 12px",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  textDecoration: "none",
  color: "inherit",
};
